import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { parseTags, getCategoryColor, isNotForSale, sortTagsAlphabetically } from '../utils/tagUtils';
import { useCart } from '../contexts/CartContext';
import { trackTagClick, trackFormatClick, trackAddToCart, trackDesignLove } from '../utils/visitorTracking';

import { API_BASE_URL } from '../config/api';

function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParamsAppliedRef = useRef(false);
  
  // Check if we came from Search page
  const fromCategoriesTags = location.state?.fromCategoriesTags || location.pathname.includes('search');
  const [design, setDesign] = useState(null);
  const [formats, setFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [compositeImageUrl, setCompositeImageUrl] = useState(null);
  const [showOriginal, setShowOriginal] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [itemAdded, setItemAdded] = useState(false);
  const [productTypeFilter, setProductTypeFilter] = useState(null); // null, 'Digital', 'Physical'
  const { addToCart } = useCart();
  
  // Mockup cycling state
  const [mockupPreviews, setMockupPreviews] = useState([]); // Array of preview URLs: [main_template, mockup1, mockup2, ...]
  const [mockupNames, setMockupNames] = useState([]); // Array of mockup names: [main_template_name, mockup1_name, ...]
  const [currentMockupIndex, setCurrentMockupIndex] = useState(0); // 0 = main_template, 1+ = mockups
  const [relatedBundles, setRelatedBundles] = useState([]); // Bundles that include this design
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [previewProgress, setPreviewProgress] = useState({ current: 0, total: 0 });
  const [formatMainTemplate, setFormatMainTemplate] = useState(null); // Store main_template data for overlay calculation
  
  // Check if user is logged in (check both localStorage and sessionStorage)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Love state
  const [isLoved, setIsLoved] = useState(false);
  const [loveCount, setLoveCount] = useState(0);
  const [isTogglingLove, setIsTogglingLove] = useState(false);
  
  // Overlay state for troubleshooting
  const [previewImageDimensions, setPreviewImageDimensions] = useState({ 
    naturalWidth: 0, 
    naturalHeight: 0, 
    displayWidth: 0, 
    displayHeight: 0 
  });
  
  // Image loading state for progressive enhancement
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(null);

  // Handle image resize for overlay calculation - trigger recalculation
  useEffect(() => {
    const handleResize = () => {
      // Force a state update to trigger overlay recalculation
      const img = document.getElementById('preview-image');
      if (img && img.complete && previewImageDimensions.naturalWidth > 0) {
        // Update dimensions to trigger overlay recalculation
        setPreviewImageDimensions(prev => ({
          ...prev,
          displayWidth: img.offsetWidth || img.clientWidth,
          displayHeight: img.offsetHeight || img.clientHeight
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [previewImageDimensions.naturalWidth]);

  // Track the last image URL to detect actual changes
  const [lastImageUrl, setLastImageUrl] = useState(null);

  // Reset dimensions only when the actual image URL changes
  useEffect(() => {
    let currentImageUrl = null;
    if (showOriginal && design?.id) {
      currentImageUrl = `${API_BASE_URL}/designs/${design.id}/image`;
    } else if (!showOriginal && mockupPreviews.length > 0 && currentMockupIndex < mockupPreviews.length) {
      currentImageUrl = mockupPreviews[currentMockupIndex];
    } else if (!showOriginal && compositeImageUrl) {
      currentImageUrl = compositeImageUrl;
    }

    // Only reset if the URL actually changed
    if (currentImageUrl && currentImageUrl !== lastImageUrl) {
      setLastImageUrl(currentImageUrl);
      setPreviewImageDimensions({ naturalWidth: 0, naturalHeight: 0, displayWidth: 0, displayHeight: 0 });
      setImageLoaded(false); // Reset image loaded state when URL changes
    }
  }, [showOriginal, mockupPreviews, currentMockupIndex, compositeImageUrl, design?.id, lastImageUrl]);
  
  
  useEffect(() => {
    const checkLogin = () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      setIsLoggedIn(!!token);
    };
    
    checkLogin();
    
    // Listen for storage changes
    const handleStorageChange = () => checkLogin();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [designsRes, formatsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/designs`),
          axios.get(`${API_BASE_URL}/formats`)
        ]);

        if (designsRes.data.success && formatsRes.data.success) {
          const foundDesign = designsRes.data.data.find(d => d.id === id);
          if (foundDesign) {
            setDesign(foundDesign);
            setFormats(formatsRes.data.data);
          } else {
            // Design not found, redirect to 404
            navigate('/note/404', { replace: true });
            return;
          }
        } else {
          setError('Failed to load design data');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        // If design not found (404), redirect to 404 page
        if (err.response?.status === 404 || err.response?.data?.error?.includes('not found')) {
          navigate('/note/404', { replace: true });
          return;
        }
        setError(err.response?.data?.error || err.message || 'Failed to load design');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  // Bundles that include this design. Non-blocking: the page works without them.
  useEffect(() => {
    let cancelled = false;
    const loadBundles = async () => {
      if (!id) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/bundles/by-design/${id}`);
        if (!cancelled && response.data.success) {
          setRelatedBundles(response.data.data || []);
        }
      } catch (err) {
        if (!cancelled) setRelatedBundles([]);
      }
    };
    loadBundles();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Apply format/mockup from URL params when design and formats are loaded (front-end deep-linking)
  useEffect(() => {
    if (loading || !design || formats.length === 0) return;

    const formatParam = searchParams.get('format');
    const mockupParam = searchParams.get('mockup');

    // Skip for mockup-only designs (no format selection)
    const isMockupOnly = design && Array.isArray(design.mockups) && design.mockups.length > 0 &&
      design.price !== undefined && design.price !== null;
    if (isMockupOnly) return;

    if (!formatParam) {
      urlParamsAppliedRef.current = null;
      return;
    }

    if (urlParamsAppliedRef.current === formatParam) return;

    const format = formats.find(f => f.id === formatParam);
    if (!format) return;

    // Check excludedFormats
    if (design.excludedFormats && Array.isArray(design.excludedFormats)) {
      for (const exclusion of design.excludedFormats) {
        if (exclusion === format.id) return;
        if (exclusion === '*Digital' && format.type === 'Digital') return;
        if (exclusion === '*Physical' && format.type === 'Physical') return;
      }
    }

    urlParamsAppliedRef.current = formatParam;

    setProductTypeFilter(format.type || 'Physical');
    setSelectedFormat(format.id);
    setQuantity(1);
    setShowOriginal(false);

    const mockupIndex = parseInt(mockupParam, 10);
    const initialMockup = (!isNaN(mockupIndex) && mockupIndex >= 0) ? mockupIndex : 0;

    const apply = async () => {
      await generateAllPreviews(format.id, initialMockup);
      await generateComposite(format.id);
    };
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- generateAllPreviews, generateComposite are async callbacks
  }, [loading, design, formats, searchParams]);

  // Reset urlParamsAppliedRef when design id changes (e.g. navigation to different design)
  useEffect(() => {
    urlParamsAppliedRef.current = null;
  }, [id]);

  // Load loved designs from localStorage and check if current design is loved
  useEffect(() => {
    if (design) {
      // Get love count from design
      setLoveCount(design.loves || 0);
      
      // Check localStorage for loved designs
      const lovedDesignsStr = localStorage.getItem('lovedDesigns');
      if (lovedDesignsStr) {
        try {
          const lovedDesigns = JSON.parse(lovedDesignsStr);
          setIsLoved(Array.isArray(lovedDesigns) && lovedDesigns.includes(design.id));
        } catch (err) {
          console.error('Error parsing lovedDesigns from localStorage:', err);
          setIsLoved(false);
        }
      } else {
        setIsLoved(false);
      }
    }
  }, [design]);

  // Filter formats based on product type filter and excludedFormats
  const filteredFormats = useMemo(() => {
    if (!productTypeFilter) return []; // Don't show any products until a filter is selected
    
    // First, filter by product type
    let filtered = [];
    if (productTypeFilter === 'Digital') {
      filtered = formats.filter(format => format.type === 'Digital');
    } else if (productTypeFilter === 'Physical') {
      filtered = formats.filter(format => format.type === 'Physical');
    } else {
      return [];
    }
    
    // Then apply excludedFormats filtering if the field exists
    if (design && design.excludedFormats && Array.isArray(design.excludedFormats) && design.excludedFormats.length > 0) {
      filtered = filtered.filter(format => {
        // Check if this format should be excluded
        for (const exclusion of design.excludedFormats) {
          // Handle wildcard exclusions
          if (exclusion === '*Digital' && format.type === 'Digital') {
            return false; // Exclude this format
          }
          if (exclusion === '*Physical' && format.type === 'Physical') {
            return false; // Exclude this format
          }
          // Handle specific format ID exclusions
          if (exclusion === format.id) {
            return false; // Exclude this format
          }
        }
        return true; // Include this format
      });
    }
    
    return filtered;
  }, [formats, productTypeFilter, design]);

  // Reset selected format if it's not in the filtered list
  useEffect(() => {
    if (selectedFormat && !filteredFormats.find(f => f.id === selectedFormat)) {
      setSelectedFormat(null);
      setCompositeImageUrl(null);
      setShowOriginal(true);
      setMockupPreviews([]);
      setMockupNames([]);
      setCurrentMockupIndex(0);
    }
  }, [productTypeFilter, selectedFormat, filteredFormats]);

  // Cycle through mockups
  const handlePreviousMockup = () => {
    if (mockupPreviews.length > 0) {
      const next = currentMockupIndex === 0 ? mockupPreviews.length - 1 : currentMockupIndex - 1;
      setCurrentMockupIndex(next);
      if (selectedFormat) {
        setSearchParams(prev => {
          const p = new URLSearchParams(prev);
          p.set('format', selectedFormat);
          p.set('mockup', String(next));
          return p;
        }, { replace: true });
      }
    }
  };

  const handleNextMockup = () => {
    if (mockupPreviews.length > 0) {
      const next = currentMockupIndex === mockupPreviews.length - 1 ? 0 : currentMockupIndex + 1;
      setCurrentMockupIndex(next);
      if (selectedFormat) {
        setSearchParams(prev => {
          const p = new URLSearchParams(prev);
          p.set('format', selectedFormat);
          p.set('mockup', String(next));
          return p;
        }, { replace: true });
      }
    }
  };

  // Ensure quantity is always 1 for digital products
  useEffect(() => {
    if (selectedFormat) {
      const formatData = formats.find(f => f.id === selectedFormat);
      if (formatData && formatData.type === 'Digital') {
        setQuantity(1);
      }
    }
  }, [selectedFormat, formats]);

  // Initialize mockup previews for mockup-only designs
  useEffect(() => {
    if (design && Array.isArray(design.mockups) && design.mockups.length > 0 && design.price !== undefined && design.price !== null) {
      // Only initialize if we haven't already set mockup previews
      if (mockupPreviews.length === 0) {
        const mockupUrls = design.mockups.map(mockup => {
          // Use image_url if available
          if (mockup.image_url) {
            // If it's already a full URL, use it as-is
            if (mockup.image_url.startsWith('http')) {
              return mockup.image_url;
            }
            // If it starts with /assets/mockups/, convert to /api/mockups/
            if (mockup.image_url.startsWith('/assets/mockups/')) {
              const filename = mockup.image_url.replace('/assets/mockups/', '');
              return `${API_BASE_URL}/mockups/${filename}`;
            }
            // If it's already /api/mockups/, just prepend the base URL
            if (mockup.image_url.startsWith('/api/mockups/')) {
              return `${API_BASE_URL.replace('/api', '')}${mockup.image_url}`;
            }
            // Otherwise, assume it's a relative path and try /api/mockups/
            return `${API_BASE_URL}/mockups/${mockup.image_url.replace(/^\//, '')}`;
          }
          return null;
        }).filter(Boolean);
        
        const mockupNames = design.mockups.map(mockup => mockup.name || 'Mockup');
        
        if (mockupUrls.length > 0) {
          setMockupPreviews(mockupUrls);
          setMockupNames(mockupNames);
          setCurrentMockupIndex(0);
          setShowOriginal(false); // Start with first mockup
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design?.id, design?.mockups?.length, design?.price]);

  const generateComposite = async (formatId) => {
    if (!design) return;

    setProcessing(true);
    setError(null);
    setShowOriginal(false);

    try {
      const response = await axios.post(`${API_BASE_URL}/fulfill`, {
        designId: design.id,
        formatId: formatId
      });

      if (response.data.success) {
        // Get the download URL and use it to display the image
        const baseUrl = API_BASE_URL.replace('/api', '');
        const imageUrl = `${baseUrl}${response.data.downloadUrl}`;
        setCompositeImageUrl(imageUrl);
        setShowOriginal(false);
      } else {
        setError(response.data.error || 'Fulfillment failed');
        setShowOriginal(true);
      }
    } catch (err) {
      console.error('Fulfillment error:', err);
      setError(err.response?.data?.error || 'Fulfillment failed');
      setShowOriginal(true);
    } finally {
      setProcessing(false);
    }
  };

  // Pre-generate all previews when format is selected. Returns total preview count for URL param handling.
  const generateAllPreviews = async (formatId, initialMockupIndex = 0) => {
    if (!design) return 0;

    setLoadingPreviews(true);
    try {
      // Fetch mockups data, filtered by design orientation
      const mockupsRes = await axios.get(`${API_BASE_URL}/formats/${formatId}/mockups?designId=${design.id}`);
      
      if (mockupsRes.data.success) {
        const { main_template, main_template_name, mockups } = mockupsRes.data.data;
        const totalPreviews = 1 + (mockups?.length || 0); // 1 for main_template + mockups
        
        // Store main_template for overlay calculation
        setFormatMainTemplate(main_template);
        
        // Generate preview URLs for all (main_template + mockups)
        const previewUrls = [];
        const names = [];
        
        // Add main template
        previewUrls.push(`${API_BASE_URL}/preview?designId=${design.id}&formatId=${formatId}&mockupIndex=0&t=${Date.now()}`);
        names.push(main_template_name || 'Main Template');
        
        // Add mockups
        for (let i = 0; i < (mockups?.length || 0); i++) {
          const previewUrl = `${API_BASE_URL}/preview?designId=${design.id}&formatId=${formatId}&mockupIndex=${i + 1}&t=${Date.now()}`;
          previewUrls.push(previewUrl);
          names.push(mockups[i]?.name || `Mockup ${i + 1}`);
        }
        
        setMockupPreviews(previewUrls);
        setMockupNames(names);
        const clampedIndex = Math.min(Math.max(0, initialMockupIndex), totalPreviews - 1);
        setCurrentMockupIndex(clampedIndex);
        
        // Pre-load all preview images with progress tracking
        setPreviewProgress({ current: 0, total: previewUrls.length });
        const imagePromises = previewUrls.map((url, index) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              setPreviewProgress(prev => ({ ...prev, current: prev.current + 1 }));
              resolve();
            };
            img.onerror = () => {
              setPreviewProgress(prev => ({ ...prev, current: prev.current + 1 }));
              resolve(); // Continue even if one fails
            };
            img.src = url;
          });
        });
        
        await Promise.all(imagePromises);
        setPreviewProgress({ current: 0, total: 0 }); // Reset progress
        return totalPreviews;
      }
    } catch (err) {
      console.error('Error generating previews:', err);
      // Fallback: still show main template if we have design and format
      if (design && formatId) {
        const fallbackUrl = `${API_BASE_URL}/preview?designId=${design.id}&formatId=${formatId}&mockupIndex=0`;
        setMockupPreviews([fallbackUrl]);
        setMockupNames(['Main Template']);
        setCurrentMockupIndex(0);
        return 1;
      } else {
        setMockupPreviews([]);
        setMockupNames([]);
        // If we can't generate previews, show original image
        setShowOriginal(true);
        return 0;
      }
    } finally {
      setLoadingPreviews(false);
    }
    return 0;
  };

  const handleFormatSelect = async (formatId) => {
    const selectedFormatData = formats.find(f => f.id === formatId);
    if (selectedFormatData) {
      // Track format click (digital/physical)
      trackFormatClick(formatId, selectedFormatData.name, selectedFormatData.type || 'Physical');
    }
    setSelectedFormat(formatId);
    setQuantity(1); // Always default to 1 (especially for digital products)
    setShowOriginal(false);

    // Update URL for shareable deep link (front-end only)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('format', formatId);
      next.delete('mockup'); // Reset mockup when changing format
      return next;
    }, { replace: true });

    // Generate all previews (main_template + mockups)
    await generateAllPreviews(formatId);
    
    // Also generate composite for fulfillment (uses main_template)
    await generateComposite(formatId);
  };

  const handleShowOriginal = () => {
    setShowOriginal(true);
    setCompositeImageUrl(null);
  };

  const handleAddToCart = () => {
    // Safety check: prevent adding NOT FOR SALE items
    if (isNotForSale(design)) {
      setError('This design is not available for purchase.');
      return;
    }

    // Handle mockup-only designs (no format selection needed)
    if (isMockupOnlyDesign) {
      if (!design.price) {
        setError('Price not set for this design.');
        return;
      }

      // Use first mockup image or original design image
      const imageUrl = mockupPreviews.length > 0 && currentMockupIndex < mockupPreviews.length
        ? mockupPreviews[currentMockupIndex]
        : `${API_BASE_URL}/designs/${design.id}/image`;

      const result = addToCart({
        designId: design.id,
        designTitle: design.title,
        designDescription: design.description,
        formatId: null, // No format for mockup-only designs
        formatName: 'Mockup Design',
        price: design.price,
        imageUrl: imageUrl,
        quantity: quantity,
        type: design.product_type || 'Physical' // Use product_type from design
      });

      if (!result.success) {
        setError(result.error);
        return;
      }

      // Track add to cart event
      trackAddToCart(
        design.id,
        design.title,
        null, // No formatId
        'Mockup Design',
        design.product_type || 'Physical',
        design.price,
        quantity
      ).catch(err => {
        console.error('[DetailPage] Failed to track add to cart:', err);
      });

      // Show success message
      setError(null);
      setItemAdded(true);
      
      // Restore UI after 2 seconds
      setTimeout(() => {
        setItemAdded(false);
      }, 2000);
      
      return;
    }

    // Handle format-based designs (existing flow)
    if (!selectedFormat) {
      setError('Please select a format first');
      return;
    }

    if (!compositeImageUrl) {
      setError('Please wait for the preview to generate');
      return;
    }

    const selectedFormatData = formats.find(f => f.id === selectedFormat);
    if (!selectedFormatData) {
      setError('Format not found');
      return;
    }

    const result = addToCart({
      designId: design.id,
      designTitle: design.title,
      designDescription: design.description,
      formatId: selectedFormat,
      formatName: selectedFormatData.name,
      price: selectedFormatData.price,
      imageUrl: compositeImageUrl,
      quantity: quantity,
      type: selectedFormatData.type || 'Physical' // Include product type
    });

    if (!result.success) {
      setError(result.error);
      return;
    }

    // Track add to cart event
    trackAddToCart(
      design.id,
      design.title,
      selectedFormat,
      selectedFormatData.name,
      selectedFormatData.type || 'Physical',
      selectedFormatData.price,
      quantity
    ).catch(err => {
      console.error('[DetailPage] Failed to track add to cart:', err);
    });

    // Show success message that replaces quantity/button section
    setError(null);
    setItemAdded(true);
    
    // Restore UI after 2 seconds
    setTimeout(() => {
      setItemAdded(false);
    }, 2000);
  };

  const handleTagClick = (tag) => {
    // Track tag click
    trackTagClick(tag);
    // Navigate back to the page we came from (or gallery) with the selected tag
    if (fromCategoriesTags) {
      // Preserve existing filters and update tag
      const existingFilters = location.state?.filters || {};
      navigate('/search', { 
        state: { 
          filters: {
            ...existingFilters,
            tag: tag,
            category: null // Clear category when selecting a tag
          }
        } 
      });
    } else {
      navigate(`/t/${encodeURIComponent(tag)}`);
    }
  };

  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    if (!design) return;
    const baseUrl = `${window.location.origin}/design/${design.id}`;
    let shareUrl = baseUrl;
    if (selectedFormat) {
      shareUrl += `?format=${encodeURIComponent(selectedFormat)}`;
      if (currentMockupIndex > 0) {
        shareUrl += `&mockup=${currentMockupIndex}`;
      }
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
      setError('Failed to copy link');
    }
  };

  const handleToggleLove = async () => {
    if (!design || isTogglingLove) return;

    setIsTogglingLove(true);
    const newLovedState = !isLoved;

    // Optimistic update
    setIsLoved(newLovedState);
    setLoveCount(prev => newLovedState ? prev + 1 : Math.max(0, prev - 1));

    // Update localStorage
    try {
      const lovedDesignsStr = localStorage.getItem('lovedDesigns');
      let lovedDesigns = lovedDesignsStr ? JSON.parse(lovedDesignsStr) : [];
      if (!Array.isArray(lovedDesigns)) {
        lovedDesigns = [];
      }

      if (newLovedState) {
        // Add to loved designs if not already present
        if (!lovedDesigns.includes(design.id)) {
          lovedDesigns.push(design.id);
        }
      } else {
        // Remove from loved designs
        lovedDesigns = lovedDesigns.filter(id => id !== design.id);
      }

      localStorage.setItem('lovedDesigns', JSON.stringify(lovedDesigns));
    } catch (err) {
      console.error('Error updating localStorage:', err);
    }

    // Call API
    try {
      const response = await axios.post(`${API_BASE_URL}/designs/${design.id}/love`, {
        action: newLovedState ? 'add' : 'remove'
      });

      if (response.data.success) {
        // Update with server response
        setLoveCount(response.data.data.loves);
        // Update design object
        setDesign(prev => prev ? { ...prev, loves: response.data.data.loves } : null);
        // Track design love for analytics (only on add)
        if (newLovedState) {
          trackDesignLove(design.id, design.title || design.id);
        }
      } else {
        // Revert optimistic update on error
        setIsLoved(!newLovedState);
        setLoveCount(prev => newLovedState ? Math.max(0, prev - 1) : prev + 1);
        setError('Failed to update love status');
      }
    } catch (err) {
      console.error('Error toggling love:', err);
      // Revert optimistic update on error
      setIsLoved(!newLovedState);
      setLoveCount(prev => newLovedState ? Math.max(0, prev - 1) : prev + 1);
      setError(err.response?.data?.error || 'Failed to update love status');
    } finally {
      setIsTogglingLove(false);
    }
  };

  // Determine display image URL and thumbnail URL for faster LCP (memoized)
  // This must be before any early returns to maintain hook order
  const { displayImageUrl, displayImageThumbnailUrl } = useMemo(() => {
    let imageUrl = null;
    let thumbnailUrl = null;
    
    if (design?.id) {
      const thumbUrl = `${API_BASE_URL}/designs/${design.id}/thumbnail`;
      const fullUrl = `${API_BASE_URL}/designs/${design.id}/image`;
      
      const isMockupOnly = design && Array.isArray(design.mockups) && design.mockups.length > 0 && design.price !== undefined && design.price !== null;
      
      if (isMockupOnly && mockupPreviews.length > 0) {
        // For mockup-only designs, show mockups or original
        if (showOriginal) {
          thumbnailUrl = thumbUrl; // Use thumbnail for LCP
          imageUrl = fullUrl; // Full image for after load
        } else if (currentMockupIndex < mockupPreviews.length) {
          imageUrl = mockupPreviews[currentMockupIndex];
        } else {
          thumbnailUrl = thumbUrl;
          imageUrl = fullUrl;
        }
      } else if (showOriginal) {
        // Use thumbnail for initial LCP, then upgrade to full image
        thumbnailUrl = thumbUrl;
        imageUrl = fullUrl;
      } else if (mockupPreviews.length > 0 && currentMockupIndex < mockupPreviews.length) {
        // Use mockup preview if available (format-based)
        imageUrl = mockupPreviews[currentMockupIndex];
      } else if (compositeImageUrl) {
        // Fallback to composite if available
        imageUrl = compositeImageUrl;
      } else {
        // Final fallback to original design image if mockups/composite not ready
        thumbnailUrl = thumbUrl;
        imageUrl = fullUrl;
      }
    }
    
    return { displayImageUrl: imageUrl, displayImageThumbnailUrl: thumbnailUrl };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- design object identity vs design?.id
  }, [design?.id, design?.mockups, design?.price, mockupPreviews, currentMockupIndex, showOriginal, compositeImageUrl]);
  
  // Preload thumbnail for faster LCP
  useEffect(() => {
    if (design?.id && showOriginal && displayImageThumbnailUrl) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = displayImageThumbnailUrl;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [design?.id, showOriginal, displayImageThumbnailUrl]);
  
  // Set initial image source (thumbnail first, then upgrade to full)
  useEffect(() => {
    if (displayImageThumbnailUrl && displayImageUrl && showOriginal) {
      // Start with thumbnail for fast LCP
      setCurrentImageSrc(displayImageThumbnailUrl);
      setImageLoaded(false); // Reset loaded state when source changes
    } else if (displayImageUrl) {
      // Use full image if no thumbnail available
      setCurrentImageSrc(displayImageUrl);
      setImageLoaded(false);
    } else {
      setCurrentImageSrc(null);
      setImageLoaded(false);
    }
  }, [displayImageThumbnailUrl, displayImageUrl, showOriginal]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading design...</p>
        </div>
      </div>
    );
  }

  if (error && !design) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => {
                if (fromCategoriesTags) {
                  // Restore filters from location state (or sessionStorage as fallback)
                  const filters = location.state?.filters || (() => {
                    try {
                      const stored = sessionStorage.getItem('categoriesTagsFilters');
                      return stored ? JSON.parse(stored) : null;
                    } catch (e) {
                      return null;
                    }
                  })();
                  navigate('/search', { state: { filters } });
                } else {
                  navigate('/');
                }
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors mr-2"
            >
              {fromCategoriesTags ? 'Back to Search' : 'Back to Gallery'}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!design) {
    return null;
  }

  const selectedFormatData = formats.find(f => f.id === selectedFormat);
  
  // Check if design is marked as NOT FOR SALE
  const notForSale = isNotForSale(design);
  
  // Check if this is a mockup-only design (has mockups array and price, no format selection needed)
  const isMockupOnlyDesign = design && Array.isArray(design.mockups) && design.mockups.length > 0 && design.price !== undefined && design.price !== null;
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[DetailPage] Image URL Debug:', {
      designId: design?.id,
      showOriginal,
      isMockupOnlyDesign,
      mockupPreviewsLength: mockupPreviews.length,
      compositeImageUrl: !!compositeImageUrl,
      displayImageUrl
    });
  }
  
  // Check if we should show navigation arrows (only if we have mockups and not showing original)
  const showMockupNavigation = !showOriginal && mockupPreviews.length > 1;

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-12 px-2 sm:px-4 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => {
            if (fromCategoriesTags) {
              // Restore filters from location state (or sessionStorage as fallback)
              const filters = location.state?.filters || (() => {
                try {
                  const stored = sessionStorage.getItem('categoriesTagsFilters');
                  return stored ? JSON.parse(stored) : null;
                } catch (e) {
                  return null;
                }
              })();
              navigate('/search', { state: { filters } });
            } else {
              navigate('/');
            }
          }}
          className="mb-3 sm:mb-4 lg:mb-6 text-blue-600 hover:text-blue-700 text-sm sm:text-base font-medium flex items-center"
        >
          <span className="mr-1 sm:mr-2">←</span> <span className="hidden sm:inline">{fromCategoriesTags ? 'Back to Search' : 'Back to Gallery'}</span><span className="sm:hidden">Back</span>
        </button>

        <div className="grid grid-cols-5 gap-2 sm:gap-3 lg:gap-8">
          {/* Design Preview */}
          <div className="col-span-3 sm:col-span-3 lg:col-span-3 mb-4 sm:mb-6 lg:mb-0">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-1.5 sm:p-3 lg:p-8">
              <div className="relative">
                {/* Original Design Button - Hide for mockup-only designs */}
                {!showOriginal && (compositeImageUrl || mockupPreviews.length > 0) && !isMockupOnlyDesign && (
                  <button
                    onClick={handleShowOriginal}
                    className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 px-2 py-1 sm:px-4 sm:py-2 bg-white border-2 border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-md"
                  >
                    <span className="hidden sm:inline">Original Design</span>
                    <span className="sm:hidden">Original</span>
                  </button>
                )}
                
                {/* Processing Overlay */}
                {(processing || loadingPreviews) && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <p className="mt-4 text-gray-600">
                        {loadingPreviews 
                          ? (previewProgress.total > 0 
                              ? `Generating previews... (${previewProgress.current}/${previewProgress.total})`
                              : 'Loading previews...')
                          : 'Generating...'}
                      </p>
                      {loadingPreviews && previewProgress.total > 0 && (
                        <div className="mt-2 w-48 mx-auto bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(previewProgress.current / previewProgress.total) * 100}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mockup Navigation Arrows */}
                {showMockupNavigation && (
                  <>
                    <button
                      onClick={handlePreviousMockup}
                      className="absolute left-1 sm:left-2 lg:left-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white bg-opacity-90 hover:bg-opacity-100 border-2 border-gray-300 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                      aria-label="Previous mockup"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNextMockup}
                      className="absolute right-1 sm:right-2 lg:right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white bg-opacity-90 hover:bg-opacity-100 border-2 border-gray-300 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                      aria-label="Next mockup"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative max-w-full">
                  {currentImageSrc ? (
                    <div className="relative w-full h-full" style={{ position: 'relative' }}>
                      {/* Loading placeholder - always rendered to reserve space */}
                      <div className={`absolute inset-0 bg-gray-200 flex items-center justify-center transition-opacity duration-300 ${
                        imageLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}>
                        <div className="text-gray-400 text-sm">Loading...</div>
                      </div>
                      <img
                        id="preview-image"
                        src={currentImageSrc}
                        alt={showOriginal ? design.title : `${design.title} - ${selectedFormatData?.name || 'Format'}`}
                        className={`w-full h-full object-contain block transition-opacity duration-300 ${
                          imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        loading="eager"
                        fetchpriority="high"
                        width="800"
                        height="800"
                        onLoad={(e) => {
                          const img = e.target;
                          
                          // If we loaded thumbnail first, now load full image
                          if (displayImageThumbnailUrl && displayImageUrl && img.src === displayImageThumbnailUrl && showOriginal) {
                            const fullImage = new Image();
                            fullImage.onload = () => {
                              setCurrentImageSrc(displayImageUrl);
                              setImageLoaded(true);
                            };
                            fullImage.onerror = () => {
                              // If full image fails, keep thumbnail
                              setImageLoaded(true);
                            };
                            fullImage.src = displayImageUrl;
                          } else {
                            setImageLoaded(true);
                          }
                          
                          // Use requestAnimationFrame to ensure dimensions are captured after render
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              // Only update if we don't already have valid dimensions
                              setPreviewImageDimensions(prev => {
                                if (prev.naturalWidth > 0 && prev.naturalHeight > 0) {
                                  // Update display dimensions only
                                  return {
                                    ...prev,
                                    displayWidth: img.offsetWidth || img.clientWidth,
                                    displayHeight: img.offsetHeight || img.clientHeight
                                  };
                                } else {
                                  // Set all dimensions
                                  return {
                                    naturalWidth: img.naturalWidth,
                                    naturalHeight: img.naturalHeight,
                                    displayWidth: img.offsetWidth || img.clientWidth,
                                    displayHeight: img.offsetHeight || img.clientHeight
                                  };
                                }
                              });
                            }, 50);
                          });
                        }}
                        onError={(e) => {
                          console.error('[DetailPage] Image load error:', {
                            src: e.target.src,
                            designId: design?.id,
                            currentImageSrc,
                            displayImageUrl
                          });
                          
                          // If thumbnail failed, try full image
                          if (displayImageThumbnailUrl && e.target.src === displayImageThumbnailUrl && displayImageUrl) {
                            setCurrentImageSrc(displayImageUrl);
                            return;
                          }
                          
                          // Try fallback image, but if that fails too, show placeholder text
                          if (e.target.src !== '/assets/0ImageNotAvailable01.png') {
                            setCurrentImageSrc('/assets/0ImageNotAvailable01.png');
                          } else {
                            setImageLoaded(false);
                            setCurrentImageSrc(null);
                          }
                        }}
                      />
                      {!showOriginal && selectedFormatData && (() => {
                        // Replicate server's getTemplateProperties and getPlacements logic
                        const getPlacementCoordinates = () => {
                          // Use the main_template data fetched from the mockups endpoint
                          const template = formatMainTemplate || selectedFormatData.main_template || selectedFormatData;
                          const designOrientation = design?.orientation || 'vertical';
                          
                          console.log('[Placement Debug] Format structure:', {
                            hasFormatMainTemplate: !!formatMainTemplate,
                            hasSelectedFormatMainTemplate: !!selectedFormatData.main_template,
                            templateKeys: template ? Object.keys(template) : [],
                            templateHorizontal: template?.horizontal,
                            templateVertical: template?.vertical,
                            templatePlacements: template?.placements,
                            designOrientation
                          });
                          
                          if (!template) {
                            console.error('[Placement Debug] No template data available!');
                            return { x: 0, y: 0, width: 470, height: 470 };
                          }
                          
                          // Check if using new simplified structure (has vertical/horizontal objects)
                          const isNewStructure = template.vertical && template.horizontal;
                          
                          let templateProps;
                          if (isNewStructure) {
                            // New simplified structure
                            templateProps = {
                              template_file_path_vertical: template.vertical.template_file_path,
                              template_file_path_horizontal: template.horizontal.template_file_path,
                              design_width: template.vertical.design_width || template.horizontal.design_width || template.design_width,
                              design_height: template.vertical.design_height || template.horizontal.design_height || template.design_height,
                              x_offset_vertical: template.vertical.x_offset,
                              y_offset_vertical: template.vertical.y_offset,
                              x_offset_horizontal: template.horizontal.x_offset,
                              y_offset_horizontal: template.horizontal.y_offset,
                              placements: template.placements || null
                            };
                          } else {
                            // Old structure (backward compatibility)
                            templateProps = {
                              template_file_path_vertical: template.template_file_path_vertical,
                              template_file_path_horizontal: template.template_file_path_horizontal,
                              design_width: template.design_width,
                              design_height: template.design_height,
                              x_offset_vertical: template.x_offset_vertical,
                              y_offset_vertical: template.y_offset_vertical,
                              x_offset_horizontal: template.x_offset_horizontal,
                              y_offset_horizontal: template.y_offset_horizontal,
                              placements: template.placements || null
                            };
                          }

                          // Step 2: Replicate getPlacements logic from server (exact match)
                          if (templateProps.placements && Array.isArray(templateProps.placements) && templateProps.placements.length > 0) {
                            const placement = templateProps.placements[0];
                            const isNewPlacementStructure = placement.vertical && placement.horizontal;
                            
                            let x, y;
                            if (isNewPlacementStructure) {
                              // New simplified structure
                              if (designOrientation === 'horizontal') {
                                x = placement.horizontal.x_offset;
                                y = placement.horizontal.y_offset;
                              } else {
                                x = placement.vertical.x_offset;
                                y = placement.vertical.y_offset;
                              }
                            } else {
                              // Old structure (backward compatibility)
                              if (designOrientation === 'horizontal') {
                                // Check if horizontal offsets exist (including 0 as a valid value)
                                if ('x_offset_horizontal' in placement) {
                                  x = placement.x_offset_horizontal;
                                } else {
                                  x = placement.x_offset_vertical;
                                }
                                if ('y_offset_horizontal' in placement) {
                                  y = placement.y_offset_horizontal;
                                } else {
                                  y = placement.y_offset_vertical;
                                }
                              } else {
                                // For vertical designs, prioritize vertical offsets
                                if ('x_offset_vertical' in placement) {
                                  x = placement.x_offset_vertical;
                                } else {
                                  x = placement.x_offset_horizontal;
                                }
                                if ('y_offset_vertical' in placement) {
                                  y = placement.y_offset_vertical;
                                } else {
                                  y = placement.y_offset_horizontal;
                                }
                              }
                            }
                            
                            return {
                              x: x,
                              y: y,
                              width: placement.design_width || templateProps.design_width,
                              height: placement.design_height || templateProps.design_height
                            };
                          }
                          
                          // Fallback to single placement using existing structure (exact server logic)
                          let xOffset, yOffset;
                          if (designOrientation === 'horizontal') {
                            xOffset = templateProps.x_offset_horizontal !== undefined ? templateProps.x_offset_horizontal : templateProps.x_offset_vertical;
                            yOffset = templateProps.y_offset_horizontal !== undefined ? templateProps.y_offset_horizontal : templateProps.y_offset_vertical;
                          } else {
                            xOffset = templateProps.x_offset_vertical !== undefined ? templateProps.x_offset_vertical : templateProps.x_offset_horizontal;
                            yOffset = templateProps.y_offset_vertical !== undefined ? templateProps.y_offset_vertical : templateProps.y_offset_horizontal;
                          }
                          
                          return {
                            x: xOffset,
                            y: yOffset,
                            width: templateProps.design_width,
                            height: templateProps.design_height
                          };
                        };

                        // Calculate overlay for troubleshooting - using server's exact placement coordinates (reserved)
                        // eslint-disable-next-line no-unused-vars
                        const _getOverlayStyle = () => {
                          if (!previewImageDimensions.naturalWidth || !previewImageDimensions.naturalHeight) {
                            return { display: 'none' };
                          }

                          // Get placement coordinates using same logic as server
                          const placement = getPlacementCoordinates();
                          const xOffset = placement.x;  // Should be 107
                          const yOffset = placement.y;   // Should be 119
                          const designWidth = placement.width;  // Should be 650
                          const designHeight = placement.height; // Should be 750

                          // Get the actual image element
                          const img = document.getElementById('preview-image');
                          if (!img) {
                            return { display: 'none' };
                          }

                          // Get the image's actual rendered size (not container size!)
                          // The image might be smaller than container due to object-contain
                          const imgWidth = img.offsetWidth || img.clientWidth;
                          const imgHeight = img.offsetHeight || img.clientHeight;
                          
                          // Calculate scale: natural image dimensions to rendered image dimensions
                          // naturalWidth/Height = template/composite dimensions
                          const scaleX = imgWidth / previewImageDimensions.naturalWidth;
                          const scaleY = imgHeight / previewImageDimensions.naturalHeight;
                          
                          // object-contain maintains aspect ratio, so use the smaller scale
                          const scale = Math.min(scaleX, scaleY);
                          
                          // Calculate actual rendered image size (may be smaller than container)
                          const renderedImgWidth = previewImageDimensions.naturalWidth * scale;
                          const renderedImgHeight = previewImageDimensions.naturalHeight * scale;
                          
                          // Calculate image offset within container (centering due to object-contain)
                          const containerWidth = img.parentElement?.offsetWidth || imgWidth;
                          const containerHeight = img.parentElement?.offsetHeight || imgHeight;
                          const imgOffsetX = (containerWidth - renderedImgWidth) / 2;
                          const imgOffsetY = (containerHeight - renderedImgHeight) / 2;

                          // Calculate overlay position: container offset + scaled coordinates
                          // Server coordinates (107, 119) are relative to template dimensions
                          const overlayLeft = imgOffsetX + (xOffset * scale);
                          const overlayTop = imgOffsetY + (yOffset * scale);
                          const overlayWidth = designWidth * scale;
                          const overlayHeight = designHeight * scale;

                          // Explicit logging of key values
                          console.log('=== RED BOX CALCULATION VALUES ===');
                          console.log('imgOffsetX (image left offset in container):', imgOffsetX.toFixed(2));
                          console.log('imgOffsetY (image top offset in container):', imgOffsetY.toFixed(2));
                          console.log('scale (natural to rendered):', scale.toFixed(6));
                          console.log('scaleX:', scaleX.toFixed(6));
                          console.log('scaleY:', scaleY.toFixed(6));
                          console.log('Server coordinates:', { x: xOffset, y: yOffset, width: designWidth, height: designHeight });
                          console.log('Scaled coordinates:', { 
                            x: (xOffset * scale).toFixed(2), 
                            y: (yOffset * scale).toFixed(2), 
                            width: overlayWidth.toFixed(2), 
                            height: overlayHeight.toFixed(2) 
                          });
                          console.log('Final overlay position:', { 
                            left: overlayLeft.toFixed(2), 
                            top: overlayTop.toFixed(2),
                            width: overlayWidth.toFixed(2),
                            height: overlayHeight.toFixed(2)
                          });
                          console.log('Image dimensions:', {
                            natural: `${previewImageDimensions.naturalWidth}x${previewImageDimensions.naturalHeight}`,
                            rendered: `${renderedImgWidth.toFixed(2)}x${renderedImgHeight.toFixed(2)}`,
                            container: `${containerWidth}x${containerHeight}`
                          });
                          console.log('===================================');

                          return {
                            position: 'absolute',
                            left: `${overlayLeft}px`,
                            top: `${overlayTop}px`,
                            width: `${overlayWidth}px`,
                            height: `${overlayHeight}px`,
                            border: '2px solid #ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            pointerEvents: 'none',
                            boxSizing: 'border-box',
                            zIndex: 50,
                            display: 'none' // Hidden on customer-facing page, code preserved for debugging
                          };
                        };

                        // Red box overlay - hidden on customer-facing page but code preserved
                        // To re-enable: change display: 'none' to display: 'block' in getOverlayStyle()
                        return null; // Not rendering overlay on customer page
                      })()}
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      {design?.id ? (
                        <div className="text-center">
                          <p className="text-sm">Loading image...</p>
                          <p className="text-xs mt-2 text-gray-500">
                            {displayImageUrl ? `URL: ${displayImageUrl}` : 'Waiting for design data...'}
                          </p>
                        </div>
                      ) : (
                        'Select a format to preview'
                      )}
                    </div>
                  )}
                </div>
                
                {/* Mockup Name Display */}
                {!showOriginal && mockupNames.length > 0 && currentMockupIndex < mockupNames.length && (
                  <div className="mt-1 sm:mt-2 text-center">
                    <p className="text-xs text-gray-500 font-light">
                      {mockupNames[currentMockupIndex]}
                    </p>
                  </div>
                )}
              </div>

              {/* Format Description */}
              {selectedFormatData && selectedFormatData.description && (
                <div className="mt-2 sm:mt-3 lg:mt-6 pt-2 sm:pt-3 lg:pt-6 border-t border-gray-200">
                  <div 
                    className="text-xs text-gray-700 leading-relaxed [&_a]:underline [&_a]:text-blue-600 [&_a:hover]:text-blue-800"
                    dangerouslySetInnerHTML={{ __html: selectedFormatData.description }}
                  />
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-2 sm:mt-3 lg:mt-6 pt-2 sm:pt-3 lg:pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 leading-relaxed">
                  <strong className="text-gray-600">Disclaimer:</strong> The image shown is a representative mockup of the final printed version. 
                  There may be slight variations in design size, or appearance in the actual printed product due to printing processes, 
                  material differences, and display settings.
                </p>
              </div>
            </div>
          </div>

          {/* Details and Format Selection */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-2 sm:p-3 lg:p-6">
              <h1 className="text-sm sm:text-base lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-1.5 sm:mb-2">
                {design.title}
              </h1>
              {design.description && (
                <div 
                  className="text-xs sm:text-sm lg:text-base text-gray-600 mb-3 sm:mb-4 lg:mb-6 [&_a]:underline [&_a]:text-blue-600 [&_a:hover]:text-blue-800 [&_strong]:font-semibold [&_em]:italic [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4"
                  dangerouslySetInnerHTML={{ __html: design.description }}
                />
              )}

              {/* Bundle callout: this design is part of one or more bundles */}
              {relatedBundles.length > 0 && (
                <div className="mb-3 sm:mb-4 lg:mb-6 space-y-2">
                  {relatedBundles.map((bundle) => (
                    <button
                      key={bundle.id}
                      onClick={() => navigate(`/bundle/${bundle.id}`)}
                      className="w-full text-left flex items-center gap-3 p-2.5 sm:p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
                    >
                      {bundle.coverImage && (
                        <img
                          src={
                            /^https?:\/\//i.test(bundle.coverImage)
                              ? bundle.coverImage
                              : `${API_BASE_URL.replace('/api', '')}${bundle.coverImage}`
                          }
                          alt=""
                          className="w-12 h-12 rounded object-cover border border-blue-200 flex-shrink-0"
                        />
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block text-[10px] sm:text-xs font-semibold text-blue-800 uppercase tracking-wide">
                          Also available in a bundle
                        </span>
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {bundle.title}
                        </span>
                        <span className="block text-xs text-gray-600">
                          {(bundle.designIds || []).length} designs · ${Number(bundle.price).toFixed(2)}
                        </span>
                      </span>
                      <span className="text-blue-600 text-sm font-medium flex-shrink-0">View →</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Tags Display */}
              {design.tags && (() => {
                const { majorCategory, minorTags } = parseTags(design.tags);
                return (
                  <div className="mb-3 sm:mb-4 lg:mb-6">
                    <h3 className="text-xs font-semibold text-gray-700 mb-1.5 sm:mb-2">Tags:</h3>
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 lg:gap-2">
                      {majorCategory && (
                        <button
                          onClick={() => handleTagClick(majorCategory)}
                          className={`px-1.5 py-0.5 sm:px-2 sm:py-0.5 lg:px-3 lg:py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80 cursor-pointer ${getCategoryColor(majorCategory)}`}
                        >
                          {majorCategory}
                        </button>
                      )}
                      {sortTagsAlphabetically(minorTags).map((tag) => (
                        <button
                          key={tag}
                          onClick={() => handleTagClick(tag)}
                          className="px-1.5 py-0.5 sm:px-2 sm:py-0.5 lg:px-3 lg:py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Love and Share Buttons */}
              <div className="mb-3 sm:mb-4 lg:mb-6 flex flex-wrap items-center gap-2">
                <button
                  onClick={handleToggleLove}
                  disabled={isTogglingLove}
                  className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    isLoved
                      ? 'bg-slate-50 text-slate-600 border-2 border-slate-300 hover:bg-slate-100'
                      : 'bg-gray-50 text-gray-600 border-2 border-gray-300 hover:bg-gray-100'
                  } ${isTogglingLove ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="text-sm sm:text-base inline-flex">
                    {isLoved ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                  </span>
                  <span className="text-xs sm:text-sm">
                    {isLoved ? 'Loved' : 'Love'}
                  </span>
                  {loveCount > 0 && (
                    <span className="text-xs font-semibold">
                      ({loveCount})
                    </span>
                  )}
                </button>
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all bg-gray-50 text-gray-600 border-2 border-gray-300 hover:bg-gray-100 cursor-pointer"
                  title={selectedFormat ? 'Copy link to this product' : 'Copy link to this design'}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="text-xs sm:text-sm">
                    {shareCopied ? 'Copied!' : 'Share This'}
                  </span>
                </button>
              </div>

              {/* Admin Edit Link */}
              {isLoggedIn && (
                <div className="mb-3 sm:mb-4 lg:mb-6 pt-2 sm:pt-3 lg:pt-4 border-t border-gray-200">
                  <a
                    href={`/admin/gallery?designId=${design.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/admin/gallery?designId=${design.id}`);
                    }}
                    className="inline-flex items-center gap-1 sm:gap-1.5 lg:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                  >
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span className="hidden sm:inline">Edit in Admin</span>
                    <span className="sm:hidden">Edit</span>
                  </a>
                </div>
              )}

              {/* NOT FOR SALE Message */}
              {notForSale ? (
                <div className="mb-3 sm:mb-4 lg:mb-6">
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3 sm:p-4 lg:p-6">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <svg
                        className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-amber-600 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div>
                        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-amber-800 mb-1 sm:mb-2">
                          Display Only
                        </h3>
                        <p className="text-xs sm:text-sm lg:text-base text-amber-700">
                          This design is for display purposes only and is not available for purchase.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isMockupOnlyDesign ? (
                /* Mockup-Only Design UI (no format selection) */
                <>
                  {/* Price Display */}
                  <div className="mb-3 sm:mb-4 lg:mb-6">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4 lg:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1">
                            Price
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {design.product_type === 'Digital' ? 'Instant Digital Download' : 'Physical Product'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600">
                            ${design.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-2 sm:mb-3 lg:mb-4 bg-red-50 border border-red-200 rounded-lg p-1.5 sm:p-2 lg:p-4">
                      <p className="text-red-600 text-xs">{error}</p>
                    </div>
                  )}

                  {/* Quantity and Add to Cart for Mockup-Only Designs */}
                  {mockupPreviews.length > 0 && (
                    <div className="mb-2 sm:mb-3 lg:mb-4">
                      {itemAdded ? (
                        /* Success Message */
                        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                          <svg
                            className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 mx-auto text-green-600 mb-1.5 sm:mb-2 lg:mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <p className="text-sm sm:text-base lg:text-lg font-semibold text-green-800">Item Added!</p>
                          <p className="text-xs sm:text-sm text-green-600 mt-1">
                            {quantity} {quantity === 1 ? 'item' : 'items'} added to cart
                          </p>
                        </div>
                      ) : (
                        /* Quantity Input and Add to Cart Button */
                        <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                          {/* Quantity Input - Only show for Physical products */}
                          {design.product_type !== 'Digital' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 sm:mb-1.5 lg:mb-2">
                                Quantity:
                              </label>
                              <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
                                <button
                                  type="button"
                                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                  className="px-1.5 py-1 sm:px-2 sm:py-1.5 lg:px-3 lg:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                  disabled={quantity <= 1}
                                >
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={quantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    setQuantity(Math.max(1, val));
                                  }}
                                  className="w-14 sm:w-16 lg:w-20 px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 text-xs sm:text-sm lg:text-base text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => setQuantity(quantity + 1)}
                                  className="px-1.5 py-1 sm:px-2 sm:py-1.5 lg:px-3 lg:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                              <p className="mt-1 sm:mt-1.5 lg:mt-2 text-xs text-gray-600">
                                Total: ${(design.price * quantity).toFixed(2)}
                              </p>
                            </div>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            onClick={handleAddToCart}
                            className="w-full px-3 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-3 bg-green-600 text-white rounded-lg text-xs sm:text-sm lg:text-base font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Add to Cart
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Format-Based Design UI (existing flow) */
                <>
                  {/* Product Type Filter */}
                  <div className="mb-3 sm:mb-4 lg:mb-6">
                    <h2 className="text-xs sm:text-sm lg:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 lg:mb-3">
                      Available Products:
                    </h2>
                    <div className="flex gap-1.5 sm:gap-2 lg:gap-3 mb-2 sm:mb-3 lg:mb-4">
                      <button
                        onClick={() => setProductTypeFilter('Digital')}
                        className={`px-1.5 py-1 sm:px-3 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs font-medium transition-colors ${
                          productTypeFilter === 'Digital'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="hidden sm:inline">Instant Digital Download</span>
                        <span className="sm:hidden">Digital</span>
                      </button>
                      <button
                        onClick={() => setProductTypeFilter('Physical')}
                        className={`px-1.5 py-1 sm:px-3 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs font-medium transition-colors ${
                          productTypeFilter === 'Physical'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Physical
                      </button>
                    </div>
                  </div>

                  {/* Format Selection */}
                  {productTypeFilter && (
                    <div className="mb-3 sm:mb-4 lg:mb-6">
                      <h2 className="text-xs sm:text-sm lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 lg:mb-4">
                        Select a product:
                      </h2>
                      {filteredFormats.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No formats available for this product type.
                        </div>
                      ) : (
                        <div className="space-y-1.5 sm:space-y-2 lg:space-y-3">
                          {filteredFormats.map((format) => (
                        <label
                          key={format.id}
                          className={`block border-2 rounded-lg p-1.5 sm:p-2 lg:p-4 cursor-pointer transition-all ${
                            selectedFormat === format.id
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="radio"
                            name="format"
                            value={format.id}
                            checked={selectedFormat === format.id}
                            onChange={(e) => {
                              if (!processing) {
                                handleFormatSelect(e.target.value);
                              }
                            }}
                            disabled={processing}
                            className="sr-only"
                          />
                          <div className="flex justify-between items-center gap-1.5 sm:gap-2">
                            <div className="flex items-center gap-1 sm:gap-1.5 lg:gap-3 flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-xs truncate">
                                {format.name}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm lg:text-lg font-semibold text-gray-900 flex-shrink-0">
                              ${format.price.toFixed(2)}
                            </div>
                          </div>
                        </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="mb-2 sm:mb-3 lg:mb-4 bg-red-50 border border-red-200 rounded-lg p-1.5 sm:p-2 lg:p-4">
                      <p className="text-red-600 text-xs">{error}</p>
                    </div>
                  )}

                  {/* Quantity and Add to Cart (only show when composite is ready) */}
                  {compositeImageUrl && !showOriginal && selectedFormatData && (
                    <div className="mb-2 sm:mb-3 lg:mb-4">
                      {itemAdded ? (
                        /* Success Message */
                        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 sm:p-4 lg:p-6 text-center">
                          <svg
                            className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 mx-auto text-green-600 mb-1.5 sm:mb-2 lg:mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <p className="text-sm sm:text-base lg:text-lg font-semibold text-green-800">Item Added!</p>
                          <p className="text-xs sm:text-sm text-green-600 mt-1">
                            {quantity} {quantity === 1 ? 'item' : 'items'} added to cart
                          </p>
                        </div>
                      ) : (
                        /* Quantity Input and Add to Cart Button */
                        <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                          {/* Quantity Input - Only show for Physical products */}
                          {selectedFormatData.type !== 'Digital' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 sm:mb-1.5 lg:mb-2">
                                Quantity:
                              </label>
                              <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
                                <button
                                  type="button"
                                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                  className="px-1.5 py-1 sm:px-2 sm:py-1.5 lg:px-3 lg:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                  disabled={quantity <= 1}
                                >
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                  </svg>
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={quantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    setQuantity(Math.max(1, val));
                                  }}
                                  className="w-14 sm:w-16 lg:w-20 px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 text-xs sm:text-sm lg:text-base text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => setQuantity(quantity + 1)}
                                  className="px-1.5 py-1 sm:px-2 sm:py-1.5 lg:px-3 lg:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                              <p className="mt-1 sm:mt-1.5 lg:mt-2 text-xs text-gray-600">
                                Total: ${(selectedFormatData.price * quantity).toFixed(2)}
                              </p>
                            </div>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            onClick={handleAddToCart}
                            className="w-full px-3 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-3 bg-green-600 text-white rounded-lg text-xs sm:text-sm lg:text-base font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Add to Cart
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetailPage;
